import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CareerFocusWithMetrics } from '../hooks/useCareerFocus';
import { useAuth } from './AuthContext';

interface FocusModeContextValue {
  isFocusMode: boolean;
  activeFocuses: CareerFocusWithMetrics[];
  currentFocus: CareerFocusWithMetrics | null;
  defaultFocus: CareerFocusWithMetrics | null;
  defaultFocusId: string | null;
  toggleFocusMode: () => void;
  setActiveFocuses: (focuses: CareerFocusWithMetrics[]) => void;
  setCurrentFocus: (focus: CareerFocusWithMetrics | null) => void;
  setDefaultFocus: (focusId: string | null) => void;
  setFocusMode: (enabled: boolean) => void;
}

const FocusModeContext = createContext<FocusModeContextValue | undefined>(undefined);

export const useFocusMode = () => {
  const context = useContext(FocusModeContext);
  if (!context) {
    throw new Error('useFocusMode must be used within a FocusModeProvider');
  }
  return context;
};

interface FocusModeProviderProps {
  children: ReactNode;
}

export const FocusModeProvider: React.FC<FocusModeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [activeFocuses, setActiveFocuses] = useState<CareerFocusWithMetrics[]>([]);
  const [currentFocus, setCurrentFocus] = useState<CareerFocusWithMetrics | null>(null);
  const [defaultFocusId, setDefaultFocusId] = useState<string | null>(null);

  // Load saved focus mode preference from localStorage
  useEffect(() => {
    if (user?.id) {
      const savedFocusMode = localStorage.getItem(`focusMode_${user.id}`);
      if (savedFocusMode !== null) {
        setIsFocusMode(savedFocusMode === 'true');
      }

      const savedDefaultFocusId = localStorage.getItem(`defaultFocusId_${user.id}`);
      if (savedDefaultFocusId) {
        setDefaultFocusId(savedDefaultFocusId);
      }
    }
  }, [user?.id]);

  // Save focus mode preference when it changes
  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(`focusMode_${user.id}`, String(isFocusMode));
    }
  }, [isFocusMode, user?.id]);

  // Save default focus ID when it changes
  useEffect(() => {
    if (user?.id && defaultFocusId) {
      localStorage.setItem(`defaultFocusId_${user.id}`, defaultFocusId);
    }
  }, [defaultFocusId, user?.id]);

  const toggleFocusMode = () => {
    setIsFocusMode(prev => !prev);
  };

  const setFocusMode = (enabled: boolean) => {
    setIsFocusMode(enabled);
  };

  const setDefaultFocus = (focusId: string | null) => {
    setDefaultFocusId(focusId);
  };

  // Calculate default focus from activeFocuses
  const defaultFocus = defaultFocusId ? activeFocuses.find(f => f.id === defaultFocusId) || null : null;

  const value: FocusModeContextValue = {
    isFocusMode,
    activeFocuses,
    currentFocus,
    defaultFocus,
    defaultFocusId,
    toggleFocusMode,
    setActiveFocuses,
    setCurrentFocus,
    setDefaultFocus,
    setFocusMode
  };

  return (
    <FocusModeContext.Provider value={value}>
      {children}
    </FocusModeContext.Provider>
  );
};